import { FC, useMemo } from 'react';

import classNames from 'classnames';

import { Badge } from '@/components';
import { PaymentOptionExtend } from '@/components/common/types';
import { ToolTip } from '@/components/ui/ToolTip';

import useFeature from '@/hooks/useFeature';
import useLocale from '@/hooks/useLocale';
import { PaymentOption } from '@/hooks/usePaymentOptions';

import { formatMoney } from '@/utils/helpers';
import interpolate from '@/utils/translations/interpolate';
import { TypeSubFlowV3Translations } from '@/utils/translations/TypeSubFlowV3Translations';

import {
  getBundleTitle,
  getSavingsLabel,
  hasMultiplePrices,
  isBigPufOption,
} from './utils';

import './RadioPayment.scss';

export interface RadioPaymentOptionProps {
  className: string;
  option: PaymentOption | PaymentOptionExtend;
  selectedOption: PaymentOption | null;
  onSelect: (option: PaymentOption) => void;
  t: TypeSubFlowV3Translations;
  drawerStyles?: boolean;
}

const RadioPaymentOption: FC<RadioPaymentOptionProps> = ({
  className,
  option,
  t,
  selectedOption,
  onSelect,
  drawerStyles,
}) => {
  const locale = useLocale();

  const isSelected = selectedOption && selectedOption.uuid === option.uuid;

  const hasSavings = option.savings > 0;

  const hasPufElbow = option.size > 1 && hasMultiplePrices(option.kits);

  const isBookBundleOption = 'isAddon' in option && option.isAddon;

  const labelBaseClassNames =
    "before:absolute before:lg:left-0 before:lg:-mt-[0] before:lg:w-7 before:lg:h-7 before:h-5 before:w-5 before:rounded-full before:border before:border-solid before:border-black before:content-['']";

  const labelSelectedClassNames =
    'after:opacity-1 before:lg:border-indigo before:border-indigo before:bg-white after:absolute after:border-[2px] after:lg:h-[17px] after:lg:w-[17px] after:h-[14px] after:w-[14px] after:rounded-full after:bg-indigo after:content-[""] relative after:top-[4px] after:lg:top-[6px] after:left-[3px] after:lg:left-[5px]';

  const isBfcm2024 = useFeature('money_enable-bfcm-2024');

  const subSquadEnableBigPuf = useFeature('sub-squad-enable-big-puf');
  const subSquadEnableDiscountDollarSavings = useFeature(
    'sub-squad-enable-discount-dollar-savings',
  );

  const isBigPuf = isBigPufOption(option.size);

  const defaultBadgeColor = isBookBundleOption || isBigPuf ? 'Lime' : 'Purple';

  const badgeColor = isBfcm2024 ? 'RedV2' : defaultBadgeColor;

  const defaultBadgeText =
    t.subflowBadgeText && !subSquadEnableBigPuf
      ? t.subflowBadgeText
      : option.badge_title;

  const optionBadgeText = isBookBundleOption
    ? t.paymentOptionsBestDeal
    : defaultBadgeText;

  const badgeText = isBfcm2024
    ? t.bfcm2024PaymentOptionsBadge
    : optionBadgeText;

  // Option size should be checked to not render savings label for Pay per kit
  const savingsLabel = getSavingsLabel({
    isBfcm2024,
    subSquadEnableBigPuf,
    subSquadEnableDiscountDollarSavings,
    size: option.size,
    t,
    savings: option.savings,
    locale,
  });

  // Added the savings to the price for BFCM 2024
  const price = isBfcm2024 ? option.price + option.savings : option.price;

  // Savings should not be visible for BFCM 2024
  const savings =
    option.savings > 0 && !isBfcm2024 ? option.price + option.savings : 0;

  const SavingsText = useMemo(() => {
    return (
      <div
        className={classNames('pr-[30px]', {
          'pl-9 md:pl-[51px] lg:pl-[45px]': !drawerStyles,
          'lg:pl-[20px] mt-1': drawerStyles,
        })}
      >
        {hasSavings ? (
          <ul
            className={classNames({
              'list-disc ml-4 lg:max-w-[180px]':
                subSquadEnableBigPuf && !drawerStyles,
              'list-none space-y-1': !subSquadEnableBigPuf,
              'text-base': drawerStyles,
            })}
          >
            <li
              dangerouslySetInnerHTML={{
                __html: savingsLabel,
              }}
            />

            {isBookBundleOption && 'singleAddonPrice' in option && (
              <li>
                {interpolate(t.paymentOptionsPrepayWithBooksBody2, {
                  price: formatMoney(
                    option.singleAddonPrice / 100,
                    locale,
                    true,
                  ),
                })}
              </li>
            )}
          </ul>
        ) : (
          <ul
            className={classNames({
              'list-disc ml-4 lg:max-w-[180px]':
                subSquadEnableBigPuf && !drawerStyles,
              'list-none space-y-1': !subSquadEnableBigPuf,
              'text-base': drawerStyles,
            })}
          >
            <li>{t.ppkText}</li>
          </ul>
        )}
      </div>
    );
  }, [
    drawerStyles,
    hasSavings,
    isBookBundleOption,
    locale,
    option,
    savingsLabel,
    subSquadEnableBigPuf,
    t.paymentOptionsPrepayWithBooksBody2,
    t.ppkText,
  ]);

  return (
    <button
      className={classNames(`${className} flex-1 p-4 w-full`, {
        'lg:max-w-[390px]': !drawerStyles,
      })}
      onClick={() => onSelect(option)}
      data-testid="radio-payment-button"
    >
      <div
        data-testid="radio-payment-container"
        className={classNames(
          'relative w-full text-left py-6 px-4 flex flex-col border-2',
          {
            'h-full': !drawerStyles,
            'border-indigo shadow-lg': isSelected,
            'border-grey-midtone bg-white': !isSelected,
            'bg-indigo-pastel': isSelected && subSquadEnableBigPuf,
            'bg-[#F6FBFF]': isSelected && !subSquadEnableBigPuf,
            'lg:min-h-[203px]': subSquadEnableBigPuf && !drawerStyles,
            'rounded-[5px]': subSquadEnableBigPuf,
            rounded: !subSquadEnableBigPuf,
          },
        )}
      >
        {(option.badge_title || isBookBundleOption) && (
          <div
            className="absolute -top-4 left-1/2 -translate-x-1/2 transform"
            data-testid="option-badge"
          >
            <Badge text={badgeText} badgeColor={badgeColor} />
          </div>
        )}
        <div
          className={classNames('flex w-full items-start', {
            'mb-2': subSquadEnableBigPuf && !drawerStyles,
            'mb-4': !subSquadEnableBigPuf,
            'justify-center': !drawerStyles,
          })}
        >
          <div className="flex w-full items-start">
            <label
              className={classNames(
                `relative w-5 h-5 lg:text-[25px] text-lg ${labelBaseClassNames} pt-px`,
                {
                  [labelSelectedClassNames]: isSelected,
                },
              )}
            />
            <div>
              <div
                data-testid="radio-payment-option-title"
                className={classNames(
                  'w-full text-[19px] font-bold pl-3 pr-6',
                  {
                    'leading-8': subSquadEnableBigPuf,
                    'lg:max-w-[210px]': subSquadEnableBigPuf && !drawerStyles,
                    'lg:px-6': !subSquadEnableBigPuf,
                    'lg:text-[25px]': !drawerStyles,
                    'ml-2': drawerStyles,
                  },
                )}
              >
                {getBundleTitle(isBookBundleOption, option.size, t)}
              </div>
              {drawerStyles && SavingsText}
            </div>
          </div>

          <div className="text-center relative">
            {hasPufElbow && (
              <ToolTip
                id="tooltip"
                classes="align-bottom absolute top-1 -left-5"
                height={17}
                width={17}
                color="indigo"
                strokeWidth={2.5}
                message={t.pufElbowTooltip}
              />
            )}
            <p
              className={classNames('text-lg font-bold', {
                'lg:text-[1.55rem]': !drawerStyles,
                'text-xl': drawerStyles,
              })}
            >
              {formatMoney(price / 100, locale, true)}
            </p>
            {savings > 0 && (
              <p
                className={classNames(
                  'text-[17px] line-through text-[#7b7d7b]',
                  {
                    'lg:mt-2 text-charcoal': subSquadEnableBigPuf,
                    'lg:text-xl': !drawerStyles,
                  },
                )}
              >
                {formatMoney(savings / 100, locale, true)}
              </p>
            )}
          </div>
        </div>

        {!drawerStyles && SavingsText}
      </div>
    </button>
  );
};

export default RadioPaymentOption;
